import React, { useEffect, useState } from "react";
import { Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import FunctionsData from "../../Functions/FunctionsData";

function MicrochipsPage({ vet_id }) {
    const [loading, setLoading] = useState(true);
    const [microchips, setMicrochips] = useState(0);
    const [redeemed, setRedeemed] = useState(0);
    const [requesting, setRequesting] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const [error, setError] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [quantity, setQuantity] = useState(10);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        let mounted = true;

        async function fetchOrdersAndTotals(noLoading = false) {
            // fetch totals
            try {
                const totalsHandler = (data) => {
                    // handle cases where API returns array or object
                    const d = Array.isArray(data) ? data[0] : data;
                    if (!mounted) return;
                    setMicrochips(d?.total_microchips ?? 0);
                    setRedeemed(d?.total_redeemed ?? 0);
                    setLoading(false);
                };

                if (noLoading) {
                    await FunctionsData.fetchDataGetNoLoading(
                        FunctionsData.getApiUrl(`/api/vet/${vet_id}/microchips`),
                        totalsHandler
                    );
                } else {
                    await FunctionsData.fetchDataGet(
                        FunctionsData.getApiUrl(`/api/vet/${vet_id}/microchips`),
                        totalsHandler,
                        loading,
                        setLoading
                    );
                }

            } catch (e) {
                // ignore
            }

            // fetch orders
            try {
                const ordersHandler = (data) => {
                    if (!mounted) return;
                    const raw = Array.isArray(data) ? data : [];
                    // normalize date fields and keys so the UI can rely on ordered_date and sent_date
                    const normalized = raw.map((o) => {
                        const ordered = o.ordered_date || o.requested_date || o.requested_at || o.ordered_at || o.created_at || null;
                        const sent = o.sent_date || o.supplied_date || o.sent_at || o.supplied_at || null;
                        // detect various delayed flags/fields and treat them as status 'delayed'
                        const delayedField = o.delayed ?? o.Delayed ?? o.is_delayed ?? o.isDelayed ?? o.delayed_flag ?? o.Delay ?? o.DelayReason ?? null;
                        const isDelayed = (v) => {
                            if (v === null || v === undefined) return false;
                            if (v === true || v === 1) return true;
                            const s = String(v).toLowerCase();
                            return s === 'yes' || s === 'true' || s === '1' || s === 'delayed';
                        };
                        const isDelayedExplicitFalse = (v) => {
                            if (v === false || v === 0) return true;
                            const s = String(v).toLowerCase();
                            return s === 'no' || s === 'false' || s === '0';
                        };

                        const apiStatus = o.status ?? o.order_status ?? 'unknown';
                        let status = apiStatus;
                        if (isDelayed(delayedField)) {
                            status = 'delayed';
                        } else if (isDelayedExplicitFalse(delayedField) && apiStatus?.toLowerCase() === 'delayed') {
                            // delayed was switched off; fall back from prior delayed to a sensible state
                            if (sent) status = 'sent';
                            else status = 'ordered';
                        } else {
                            status = apiStatus; // keep whatever the API sent when not delayed
                        }

                        return {
                            ...o,
                            requested_quantity: o.requested_quantity ?? o.quantity ?? o.requested_qty ?? 0,
                            supplied_quantity: o.supplied_quantity ?? o.supplied_qty ?? o.supplied_quantity ?? o.supplied ?? null,
                            status,
                            ordered_date: ordered,
                            sent_date: sent
                        };
                    });
                    setOrders(normalized);
                };
                if (noLoading) {
                    await FunctionsData.fetchDataGetNoLoading(
                        FunctionsData.getApiUrl(`/api/vet/${vet_id}/microchips/orders`),
                        ordersHandler
                    );
                } else {
                    await FunctionsData.fetchDataGet(
                        FunctionsData.getApiUrl(`/api/vet/${vet_id}/microchips/orders`),
                        ordersHandler,
                        true,
                        () => {}
                    );
                }
            } catch (e) {
                // ignore
            }
        }

        // initial load
        fetchOrdersAndTotals(false);

        // poll for updates so staff "Sent" actions reflect in vet portal
        const interval = setInterval(() => fetchOrdersAndTotals(true), 30000);

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [vet_id, requestSent]);

    // helper to format dates safely
    function formatDate(d) {
        if (!d) return '-';
        try {
            return new Date(d).toLocaleString();
        } catch (e) {
            return String(d);
        }
    }

    const canRequest = microchips < 10 || microchips < Math.ceil((redeemed + microchips) * 0.05);

    const handleRequest = async () => {
        setRequesting(true);
        setError(null);
        try {
            const tkn = await FunctionsData.getCSRFToken();
            const res = await fetch(FunctionsData.getApiUrl(`/api/vet/${vet_id}/microchips/request`), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": FunctionsData.getSessionToken(),
                    "X-CSRF-Token": tkn.csrf
                },
                body: JSON.stringify({ vet_id, quantity })
            });
            const data = await res.json();
            if (data.success) {
                setRequestSent(true);
                setOpenModal(false);
            } else setError("Request failed. Please try again.");
        } catch (e) {
            setError("Request failed. Please try again.");
        }
        setRequesting(false);
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div style={{padding: 20}}>
            <h2>Microchips</h2>
            <div>Total microchips supplied: <b>{microchips}</b></div>
            <div>Total vouchers redeemed: <b>{redeemed}</b></div>
            {canRequest && !requestSent && (
                <div style={{marginTop: 20}}>
                    <Alert severity="warning" style={{marginBottom: 10}}>
                        You have less than 10 microchips or less than 5% remaining. You can request more microchips.
                    </Alert>
                    <Button variant="contained" onClick={() => setOpenModal(true)} disabled={requesting}>
                        Request More Microchips
                    </Button>
                    {error && <Alert severity="error" style={{marginTop: 10}}>{error}</Alert>}
                </div>
            )}
            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle>Request More Microchips</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Quantity"
                        type="number"
                        value={quantity}
                        onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        fullWidth
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 1 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Cancel</Button>
                    <Button onClick={handleRequest} variant="contained" disabled={requesting}>
                        {requesting ? "Requesting..." : "Request"}
                    </Button>
                </DialogActions>
            </Dialog>
            {requestSent && (
                <Alert severity="success" style={{marginTop: 20}}>
                    Request sent! The Desex Team will review your request.
                </Alert>
            )}
            <h3 style={{marginTop: 40}}>Microchip Order History</h3>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>No. of Microchips Requested</TableCell>
                            <TableCell>Microchips Supplied</TableCell>
                            <TableCell>Order Status</TableCell>
                            <TableCell>Ordered Date</TableCell>
                            <TableCell>Sent Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No orders found.</TableCell>
                            </TableRow>
                        ) : (
                            orders.map((order, idx) => (
                                <TableRow key={order.id || idx}>
                                    <TableCell>{order.requested_quantity}</TableCell>
                                    <TableCell>{order.supplied_quantity ?? '-'}</TableCell>
                                    <TableCell>{order.status}</TableCell>
                                    <TableCell>{formatDate(order.ordered_date)}</TableCell>
                                    <TableCell>{formatDate(order.sent_date)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

export default MicrochipsPage;
